import * as React from "react";

import { faAngleDown, faAngleUp, faXmark } from "@performyard/fontawesome/pro-regular-svg-icons";

import { SideNavGroup } from "./components/SideNavGroup";

import type { SideNavItem, SideNavProps } from "./types";

import { cn } from "../../../lib/utils";

import { Icon } from "../../atoms/Icon";
import { MenuItem } from "../../atoms/MenuItem";
import { Popover, PopoverTrigger } from "../../molecules/Popover";
import { UserMenu } from "../UserMenu";
import { buildSideNavThemeStyle } from "./buildThemeStyle";

/** Default DOM id for the root `<aside>`. Shared with `TopNav`'s hamburger `aria-controls`. */
export const SIDE_NAV_DOM_ID = "side-nav";

const containsActive = (items: SideNavItem[], activeKey: string | null | undefined): boolean => {
	if (!activeKey) return false;
	for (const item of items) {
		if (item.id === activeKey) return true;
		if (item.type === "group" && containsActive(item.children, activeKey)) return true;
	}
	return false;
};

const renderChild = (
	item: SideNavItem,
	activeKey: string | null | undefined,
	onItemClick?: (id: string) => void
): React.ReactNode => {
	if (item.type === "category") {
		return <MenuItem key={item.id} variant="category" label={item.label} />;
	}
	if (item.type === "group") {
		return <MenuItem key={item.id} variant="nested-link" label={item.label} />;
	}
	return (
		<MenuItem
			key={item.id}
			variant="nested-link"
			label={item.label}
			href={item.href}
			fullPageLoad={item.fullPageLoad}
			isExternal={item.isExternal}
			isActive={item.id === activeKey}
			onClick={() => onItemClick?.(item.id)}
		/>
	);
};

const renderTopLevel = (
	item: SideNavItem,
	activeKey: string | null | undefined,
	onItemClick?: (id: string) => void
): React.ReactNode => {
	if (item.type === "category") {
		return <MenuItem key={item.id} variant="category" label={item.label} />;
	}
	if (item.type === "group") {
		const groupActive = containsActive(item.children, activeKey);
		return (
			<SideNavGroup
				key={item.id}
				id={item.id}
				label={item.label}
				icon={item.icon}
				defaultOpen={groupActive || item.defaultOpen}
			>
				{item.children.map((child) => renderChild(child, activeKey, onItemClick))}
			</SideNavGroup>
		);
	}
	return (
		<MenuItem
			key={item.id}
			variant="link"
			label={item.label}
			href={item.href}
			iconLeft={item.icon}
			fullPageLoad={item.fullPageLoad}
			isExternal={item.isExternal}
			isActive={item.id === activeKey}
			onClick={() => onItemClick?.(item.id)}
		/>
	);
};

const FOCUSABLE_SELECTOR = [
	"a[href]",
	"button:not([disabled])",
	'[tabindex]:not([tabindex="-1"])',
	"input:not([disabled])",
	"select:not([disabled])",
	"textarea:not([disabled])",
].join(",");

export const SideNav = React.forwardRef<HTMLElement, SideNavProps>((props, ref) => {
	const {
		items,
		id = SIDE_NAV_DOM_ID,
		activeKey = null,
		theme,
		user,
		avatar,
		logo,
		userMenuOptions,
		onItemClick,
		onClose,
		isMobileOverlay = false,
		dangerouslySetClassName,
		"data-testid": dataTestId = "side-nav",
	} = props;

	const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
	const themeStyle = React.useMemo(() => buildSideNavThemeStyle(theme), [theme]);

	const internalRef = React.useRef<HTMLElement | null>(null);
	const setRefs = React.useCallback(
		(node: HTMLElement | null) => {
			internalRef.current = node;
			if (typeof ref === "function") ref(node);
			else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = node;
		},
		[ref]
	);

	// Ref keeps the overlay effect's deps at `[isMobileOverlay]` regardless of onClose identity.
	const onCloseRef = React.useRef(onClose);
	React.useEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);

	// Escape on `document` (survives focus drift); Tab on `aside` (avoids fighting Radix Popover).
	React.useEffect(() => {
		if (!isMobileOverlay) return;
		const aside = internalRef.current;
		if (!aside) return;

		const previouslyFocused = document.activeElement as HTMLElement | null;
		aside.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();

		const handleEscape = (event: KeyboardEvent) => {
			// Skip if a Radix layer above already handled it.
			if (event.key !== "Escape" || event.defaultPrevented) return;
			event.preventDefault();
			onCloseRef.current?.();
		};

		const handleTab = (event: KeyboardEvent) => {
			if (event.key !== "Tab") return;
			const focusableElements = Array.from(aside.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
			if (focusableElements.length === 0) {
				event.preventDefault();
				return;
			}
			const first = focusableElements[0];
			const last = focusableElements[focusableElements.length - 1];
			const active = document.activeElement as HTMLElement | null;
			const inside = active ? aside.contains(active) : false;
			if (event.shiftKey && (!inside || active === first)) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && (!inside || active === last)) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", handleEscape);
		aside.addEventListener("keydown", handleTab);
		return () => {
			document.removeEventListener("keydown", handleEscape);
			aside.removeEventListener("keydown", handleTab);
			// Skip if opener was unmounted — `.focus()` on a detached node dumps to body.
			if (
				previouslyFocused &&
				document.contains(previouslyFocused) &&
				typeof previouslyFocused.focus === "function"
			) {
				previouslyFocused.focus();
			}
		};
	}, [isMobileOverlay]);

	return (
		<aside
			id={id}
			ref={setRefs}
			className={cn(
				"box-border flex h-full w-full shrink-0 flex-col overflow-hidden border-r border-solid",
				"border-[color:var(--sidenav-divider,transparent)]",
				dangerouslySetClassName
			)}
			style={themeStyle}
			role={isMobileOverlay ? "dialog" : undefined}
			aria-modal={isMobileOverlay ? true : undefined}
			aria-label="Primary navigation"
			data-testid={dataTestId}
		>
			{logo || onClose ? (
				<header data-testid="sidenav-header" className="relative flex h-12 shrink-0 items-center px-4">
					{logo ? <div className="flex min-w-0 flex-1 items-center">{logo}</div> : null}
					{onClose ? (
						<button
							type="button"
							onClick={onClose}
							aria-label="Close navigation"
							data-testid="sidenav-close"
							className="inline-flex size-8 shrink-0 cursor-pointer appearance-none items-center justify-center rounded-md border-0 bg-transparent text-[color:var(--sidenav-fg,currentColor)] hover:bg-[color:var(--sidenav-hover-bg,transparent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--sidenav-fg,currentColor)] md:hidden"
						>
							<Icon icon={faXmark} size={20} color={null} />
						</button>
					) : null}
				</header>
			) : null}
			<nav
				aria-label="Primary navigation links"
				className={cn(
					"flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden pb-4 pt-6",
					"[&::-webkit-scrollbar]:w-1.5",
					"[&::-webkit-scrollbar-track]:bg-transparent",
					"[&::-webkit-scrollbar-thumb]:rounded-full",
					"[&::-webkit-scrollbar-thumb]:bg-[color:var(--sidenav-divider,rgba(255,255,255,0.2))]"
				)}
				style={{ scrollbarWidth: "thin", scrollbarColor: "var(--sidenav-divider) transparent" }}
			>
				{items.map((item) => renderTopLevel(item, activeKey, onItemClick))}
			</nav>

			<footer className="relative flex shrink-0 items-center gap-4 border-t border-solid border-[color:var(--sidenav-divider,rgba(255,255,255,0.2))] p-4">
				<Popover open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen}>
					<div className="flex min-w-0 flex-1 items-center gap-4">
						<div className="shrink-0">{avatar}</div>
						<div className="flex min-w-0 flex-col gap-0.5">
							<p className="truncate text-paragraph-1 font-semibold leading-6">{user.name}</p>
							<p className="truncate text-caption font-normal leading-4">{user.role}</p>
							{user.email ? (
								<p className="truncate text-caption font-normal leading-4">{user.email}</p>
							) : null}
						</div>
					</div>
					<PopoverTrigger asChild>
						<button
							type="button"
							className="flex shrink-0 cursor-pointer appearance-none flex-col items-center justify-center border-0 bg-transparent p-1 text-[color:var(--sidenav-fg,currentColor)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--sidenav-fg,currentColor)]"
							aria-label="Open user menu"
							aria-haspopup="menu"
							data-testid="sidenav-user-menu-trigger"
						>
							<Icon icon={faAngleUp} size={16} color={null} />
							<Icon icon={faAngleDown} size={16} color={null} />
						</button>
					</PopoverTrigger>
					{userMenuOptions && userMenuOptions.length > 0 ? (
						<UserMenu
							options={userMenuOptions}
							onSelect={() => setIsUserMenuOpen(false)}
							side="right"
							align="end"
							sideOffset={24}
							avoidCollisions={false}
							data-testid="sidenav-user-menu"
							optionTestIdPrefix="sidenav-user-menu-option"
							theme={theme}
						/>
					) : null}
				</Popover>
			</footer>
		</aside>
	);
});

SideNav.displayName = "SideNav";

export type { SideNavItem, SideNavProps, SideNavThemeTokens, SideNavUser, SideNavUserMenuOption } from "./types";
export { SideNavGroup };
export { buildSideNavThemeStyle };
